import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { useAuth } from "@/lib/auth-context";
import type { DealCommentRow } from "./use-deal-comments";

export type AddDealCommentInput = {
  dealId: string;
  content: string;
};

type Context = {
  previous: DealCommentRow[] | undefined;
  key: ReturnType<typeof queryKeys.deals.comments>;
};

// POST /api/portal/deals/:dealId/comments
//
// optimistic: append a placeholder so the partner sees their message instantly.
// The synthetic row carries `pending: true` and an `id: temp-<uuid>` so the
// thread UI can render it distinctly. onSettled invalidates the list — the
// server response is authoritative and replaces the temp.
//
// Pattern A (list-append) — see MIGRATION.md "Optimistic-update reference".
// Buckets 5, 7, 8, 10, 11, 12 copy this shape.
export function useAddDealComment() {
  const queryClient = useQueryClient();
  const { session } = useAuth();
  return useMutation<DealCommentRow, ApiClientError, AddDealCommentInput, Context>({
    mutationFn: ({ dealId, content }) =>
      apiFetch<DealCommentRow>(`/portal/deals/${dealId}/comments`, {
        method: "POST",
        body: JSON.stringify({ content }),
      }),
    onMutate: async ({ dealId, content }) => {
      const key = queryKeys.deals.comments(dealId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<DealCommentRow[]>(key);
      const optimistic: DealCommentRow = {
        id: `temp-${crypto.randomUUID()}`,
        deal_id: dealId,
        author_id: session?.userId ?? "",
        author_type: "partner",
        content,
        created_at: new Date().toISOString(),
        pending: true,
      };
      queryClient.setQueryData<DealCommentRow[]>(key, (old) => [...(old ?? []), optimistic]);
      return { previous, key };
    },
    onError: (err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(context.key, context.previous);
      }
      toast.error(err.message ?? "Failed to post comment");
    },
    onSettled: (_data, _err, { dealId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.deals.comments(dealId) });
    },
  });
}
